import { useState, useRef, useEffect } from "react";
import { supabase } from "./lib/supabase";

const SYSTEM_PROMPT = `You are the 6zero.ai AI — a sharp real estate advisor. You have access to property data automatically. Never mention data sources. Never show where data comes from. Just use it and sound like you know the property.

Capabilities:
1. VALUATION — Estimate fair market value from property data and comps
2. DEAL ANALYSIS — Is this a good deal? Give a BUY/PASS/NEGOTIATE verdict
3. OFFER BUILDER — Draft offers with specific numbers and contingencies

When property data is provided automatically (beds, baths, sqft, year built, tax history), use it immediately — do not ask for info you already have.

Rules:
- Max 120 words per response. Be direct. Numbers over words.
- Bullet points only. No paragraphs.
- Give specific dollar amounts, not ranges, when asked.
- Flag red flags clearly.
- End every response with one NEXT STEP.
- When user asks for listings or has shared full preferences (location, budget, beds/baths), immediately web search Redfin or Zillow for active listings matching their criteria. Return 3 specific listings with address, price, sqft, and days on market. Never tell the user to go search themselves.
- When user selects a specific property or complex, web search for the exact active listing to get real list price, DOM, and unit details before giving verdict.
- If listing price is given, immediately give BUY/PASS/NEGOTIATE verdict.`;

const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "sixzero2026";
const BETA_KEY = "od_beta_token";

const track = (name, payload = {}) => {
  fetch("/api/events", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
    body: JSON.stringify({ name, payload }),
    keepalive: true,
  }).catch(() => {});
};

// Tiny UA parser — good enough; we keep raw user_agent on the row too.
const parseUA = (ua) => {
  const out = { browser: null, browser_version: null, os: null, os_version: null, device_type: "desktop" };
  if (!ua) return out;
  if (/iPhone|iPod/.test(ua)) out.device_type = "mobile";
  else if (/iPad|Tablet/.test(ua)) out.device_type = "tablet";
  else if (/Android/.test(ua)) out.device_type = /Mobile/.test(ua) ? "mobile" : "tablet";
  const browsers = [
    [/Edg\/([\d.]+)/, "Edge"],
    [/OPR\/([\d.]+)/, "Opera"],
    [/Chrome\/([\d.]+)/, "Chrome"],
    [/Firefox\/([\d.]+)/, "Firefox"],
    [/Version\/([\d.]+).*Safari/, "Safari"],
  ];
  for (const [re, name] of browsers) {
    const m = ua.match(re);
    if (m) { out.browser = name; out.browser_version = m[1]; break; }
  }
  const oses = [
    [/Windows NT ([\d.]+)/, "Windows"],
    [/Mac OS X ([\d_.]+)/, "macOS"],
    [/Android ([\d.]+)/, "Android"],
    [/iPhone OS ([\d_]+)/, "iOS"],
    [/CPU OS ([\d_]+)/, "iOS"],
    [/Linux/, "Linux"],
  ];
  for (const [re, name] of oses) {
    const m = ua.match(re);
    if (m) { out.os = name; out.os_version = (m[1] || "").replace(/_/g, "."); break; }
  }
  return out;
};

const captureLoginForensics = (source, email = null) => {
  const ua = navigator.userAgent || "";
  const parsed = parseUA(ua);
  const params = new URLSearchParams(window.location.search);
  const payload = {
    source,
    email,
    ...parsed,
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    device_pixel_ratio: window.devicePixelRatio,
    language: navigator.language,
    languages: navigator.languages ? Array.from(navigator.languages) : null,
    client_tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || null,
    url: window.location.href,
    utm_source:   params.get("utm_source"),
    utm_medium:   params.get("utm_medium"),
    utm_campaign: params.get("utm_campaign"),
    utm_term:     params.get("utm_term"),
    utm_content:  params.get("utm_content"),
  };
  fetch("/api/login", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {});
};

const STARTER_MODES = [
  { id:"listing", icon:"⌂", label:"Analyze a property", description:"Considering buying a specific listing?", prompt:"I have a specific listing I want to analyze. Let me share the details.", color:"#e8f4f0", accent:"#2d7a62" },
  { id:"preferences", icon:"◎", label:"I'm a buyer", description:"Find what fits your criteria", prompt:"I don't have a specific listing yet. I want to share my preferences and get guidance on what to look for and what a fair price looks like.", color:"#f0eef8", accent:"#5c4db1" },
  { id:"seller", icon:"◈", label:"I'm selling", description:"Price, prepare & negotiate", prompt:"I'm selling my home and want to price it right, understand my leverage, and handle offers without paying a listing agent.", color:"#fef4e8", accent:"#c47c1a" },
  { id:"offer", icon:"✦", label:"Build an offer", description:"Draft & negotiate terms", prompt:"I want to make an offer on a property. Help me build a strong offer with the right numbers and contingencies.", color:"#fdeef0", accent:"#c0354a" },
];

const QUICK = {
  valuation:["What's a fair price?","Run comps","Is this overpriced?"],
  deal:["Good deal?","My leverage points","Any red flags?"],
  offer:["Draft my offer","Suggest contingencies","Counter-offer strategy"],
};

const GLOBAL_CSS = `
  @keyframes rise{from{opacity:0;transform:translateY(9px)}to{opacity:1;transform:translateY(0)}}
  @keyframes tdot{0%,60%,100%{transform:translateY(0);opacity:0.3}30%{transform:translateY(-6px);opacity:1}}
  @keyframes fadein{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  @keyframes blob{0%,100%{border-radius:60% 40% 55% 45%/50% 60% 40% 50%}50%{border-radius:40% 60% 45% 55%/60% 40% 50% 60%}}
  *{box-sizing:border-box}
  body{margin:0;background:#f2f8f5}
  ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-thumb{background:rgba(45,122,98,0.2);border-radius:3px}
  textarea{resize:none;background:transparent;border:none;color:#1c2b25;font-family:'Outfit',sans-serif;font-weight:400;font-size:14px;line-height:1.55;width:100%}
  textarea:focus{outline:none}
  textarea::placeholder{color:#9bbfb2}
  input:focus{outline:none}
  input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:20px;height:20px;border-radius:50%;background:#2b8259;cursor:pointer;box-shadow:0 0 10px rgba(43,130,89,0.3)}
  input[type=range]::-moz-range-thumb{width:20px;height:20px;border-radius:50%;background:#2b8259;cursor:pointer;border:none}
  .chip{transition:all 0.18s !important}
  .chip:hover{background:#1c2b25 !important;color:#d4f5e8 !important;border-color:#1c2b25 !important}
  .stab{transition:all 0.18s !important}
  .mcard{transition:all 0.22s ease !important;cursor:pointer;}
  .mcard:hover{transform:translateY(-4px) !important;box-shadow:0 14px 40px rgba(0,0,0,0.12) !important;}
  .btn-p{transition:background 0.18s !important}
  .btn-p:hover{background:#2a5c48 !important}
  .btn-g{transition:all 0.18s !important}
  .btn-g:hover{border-color:#1c2b25 !important;color:#1c2b25 !important}
  @media (max-width: 720px){
    /* prevent iOS Safari from auto-zooming on input focus */
    textarea, input[type="text"], input[type="email"], input[type="number"], input[type="password"], input:not([type]){font-size:16px !important}
  }
`;

const Logo = ({ size=30 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <rect width="40" height="40" rx="10" fill="#1c2b25"/>
    <path d="M20 8L7 18V33H16V25H24V33H33V18L20 8Z" fill="#7ecfb0"/>
    <path d="M20 8L7 18V33H16V25H24V33H33V18L20 8Z" fill="url(#lg)" opacity="0.5"/>
    <rect x="17" y="25" width="6" height="8" fill="#1c2b25" opacity="0.4"/>
    <defs>
      <linearGradient id="lg" x1="7" y1="8" x2="33" y2="33" gradientUnits="userSpaceOnUse">
        <stop stopColor="#b4f0d8"/>
        <stop offset="1" stopColor="#7ecfb0" stopOpacity="0"/>
      </linearGradient>
    </defs>
  </svg>
);

// Consistent brand label used everywhere
const BrandLabel = () => (
  <div style={{ fontFamily:"'DM Mono',monospace", fontSize:13, letterSpacing:"0.2em", color:"#2b8259", fontWeight:700 }}>6zero.ai</div>
);

// Lightweight viewport hook — re-renders when crossing the breakpoint.
const useIsMobile = (bp = 720) => {
  const [m, setM] = useState(() => typeof window !== "undefined" && window.innerWidth < bp);
  useEffect(() => {
    const onR = () => setM(window.innerWidth < bp);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, [bp]);
  return m;
};

// ─── LANDING PAGE ─────────────────────────────────────────────────────────────
function LandingPage({ onJoinBeta }) {
  const [price, setPrice] = useState(1000000);
  const agentFee = Math.round(price * 0.06);
  const isMobile = useIsMobile(820);

  const features = [
    ["Agent Commission","$0","6% of Home Value","6% (Routes to Agents)"],
    ["Direct Buyer-to-Seller","✓ Yes","No (Middleman Required)","No (Sells Leads)"],
    ["Legal & Disclosures","✓ AI Automated","Manual Paperwork","Outsourced to Agent"],
    ["AI Deal Analysis","✓ BUY/PASS/NEGOTIATE","Agent's Opinion","Not Available"],
  ];

  return (
    <div style={{ minHeight:"100vh", background:"#eef3ef", fontFamily:"'Inter',sans-serif", color:"#1a3628" }}>
      <style>{GLOBAL_CSS}</style>

      {/* Nav */}
      <nav style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:isMobile?"18px 18px":"28px 40px", maxWidth:1200, margin:"0 auto", gap:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:isMobile?10:14, minWidth:0 }}>
          <Logo size={isMobile?36:44}/>
          <div style={{ minWidth:0 }}>
            <BrandLabel/>
            {!isMobile && <div style={{ fontSize:11, color:"#5b7365" }}>Real estate, without the middleman</div>}
          </div>
        </div>
        {!isMobile && (
          <div style={{ display:"flex", gap:32, fontSize:14, fontWeight:500, color:"#5b7365" }}>
            <span style={{ cursor:"pointer" }}>Platform</span>
            <span style={{ cursor:"pointer" }}>For Buyers</span>
            <span style={{ cursor:"pointer" }}>For Sellers</span>
            <span style={{ cursor:"pointer", color:"#2b8259" }} onClick={() => window.location.href="/join"}>For Vendors</span>
            <span style={{ cursor:"pointer" }} onClick={() => window.location.href="/marketplace"}>Marketplace</span>
          </div>
        )}
        <div style={{ display:"flex", gap:8, flexShrink:0 }}>
          <button onClick={() => window.location.href="/join"} style={{ background:"transparent", color:"#2b8259", border:"1.5px solid #2b8259", borderRadius:50, padding:isMobile?"9px 14px":"11px 20px", fontWeight:700, fontSize:isMobile?12:13, cursor:"pointer" }}>
            List your services
          </button>
          <button onClick={onJoinBeta} style={{ background:"#2b8259", color:"#fff", border:"none", borderRadius:50, padding:isMobile?"10px 18px":"12px 28px", fontWeight:700, fontSize:isMobile?13:14, cursor:"pointer" }}>
            Join Beta
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding:isMobile?"32px 18px 44px":"60px 40px 80px", maxWidth:1200, margin:"0 auto", display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:isMobile?32:60, alignItems:"center" }}>
        <div>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#e1eee6", border:"1px solid #b2d4c0", borderRadius:20, padding:"5px 14px", marginBottom:20 }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:"#2b8259" }}/>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:"0.14em", color:"#2b8259", fontWeight:700, textTransform:"uppercase" }}>Private Beta</span>
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(36px,7vw,64px)", fontWeight:700, lineHeight:1.1, color:"#152d21", margin:"0 0 8px", letterSpacing:"-0.02em" }}>
            Buy. Sell. Negotiate.
          </h1>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(36px,7vw,64px)", fontWeight:700, lineHeight:1.1, color:"#2b8259", fontStyle:"italic", margin:"0 0 22px" }}>
            No agent required.
          </h1>
          <p style={{ fontSize:isMobile?15:17, color:"#5b7365", lineHeight:1.7, maxWidth:460, marginBottom:30, fontWeight:500 }}>
            6zero.ai handles the heavy lifting. Our AI automates your pricing, legal forms, and offer writing so you can skip the 6% fee and keep your equity.
          </p>
          <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
            <button onClick={onJoinBeta} style={{ background:"#2b8259", color:"#fff", border:"none", borderRadius:12, padding:isMobile?"14px 22px":"16px 32px", fontWeight:700, fontSize:15, cursor:"pointer", boxShadow:"0 8px 24px rgba(43,130,89,0.3)" }}>
              Buy or Sell →
            </button>
            <button onClick={() => window.location.href="/marketplace"} style={{ background:"#fff", color:"#152d21", border:"none", borderRadius:12, padding:isMobile?"14px 22px":"16px 32px", fontWeight:700, fontSize:15, cursor:"pointer", boxShadow:"0 4px 16px rgba(21,45,33,0.08)" }}>
              Browse Marketplace
            </button>
            <button onClick={() => window.location.href="/join"} style={{ background:"transparent", color:"#2b8259", border:"2px solid #2b8259", borderRadius:12, padding:isMobile?"13px 22px":"15px 32px", fontWeight:700, fontSize:15, cursor:"pointer" }}>
              List your services
            </button>
          </div>
        </div>

        {/* Calculator */}
        <div style={{ background:"#fff", borderRadius:isMobile?20:28, padding:isMobile?22:36, boxShadow:"0 10px 40px rgba(21,45,33,0.08)" }}>
          <div style={{ marginBottom:isMobile?22:28, paddingBottom:isMobile?22:28, borderBottom:"1px solid #f0f4f2" }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:14, fontSize:13, fontWeight:700, color:"#5b7365" }}>
              <span>Home Sale Price</span>
              <span style={{ color:"#152d21", fontFamily:"'DM Mono',monospace", fontSize:isMobile?15:17 }}>${price.toLocaleString()}</span>
            </div>
            <input type="range" min="500000" max="2000000" step="10000" value={price}
              onChange={e => setPrice(parseInt(e.target.value))}
              style={{ width:"100%", height:6, borderRadius:6, background:"#d3dfd8", appearance:"none", outline:"none", cursor:"pointer" }}
            />
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"#5b7365", marginTop:8, fontFamily:"'DM Mono',monospace" }}>
              <span>$500k</span><span>$2M</span>
            </div>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, gap:12 }}>
            <span style={{ fontSize:11, fontWeight:700, color:"#5b7365", letterSpacing:"0.08em", textTransform:"uppercase" }}>Traditional 6%</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:isMobile?15:17, color:"#f87171", textDecoration:"line-through" }}>${agentFee.toLocaleString()}</span>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:isMobile?22:28, paddingBottom:isMobile?22:28, borderBottom:"1px solid #f0f4f2", gap:12 }}>
            <span style={{ fontSize:11, fontWeight:700, color:"#152d21", letterSpacing:"0.08em", textTransform:"uppercase" }}>6zero.ai Fee</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:isMobile?17:20, color:"#2b8259", fontWeight:700 }}>$0</span>
          </div>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:11, fontWeight:700, color:"#5b7365", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8 }}>Your Equity Saved</div>
            <div style={{ fontFamily:"'Playfair Display',serif", fontSize:isMobile?42:56, fontWeight:700, color:"#2b8259", letterSpacing:"-0.02em" }}>${agentFee.toLocaleString()}</div>
          </div>
          <div style={{ marginTop:18, background:"#eef3ef", borderRadius:12, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"center", gap:8, color:"#2b8259", fontWeight:700, fontSize:13 }}>
            <span>✓</span><span>Clear to Close</span>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section style={{ padding:isMobile?"40px 18px":"60px 40px", maxWidth:1200, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:isMobile?28:48 }}>
          <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(26px,5.5vw,46px)", fontWeight:700, color:"#152d21", margin:"0 0 14px", lineHeight:1.15 }}>
            The legacy market is broken.<br/><span style={{ color:"#2b8259", fontStyle:"italic" }}>We built the bypass.</span>
          </h2>
          <p style={{ fontSize:isMobile?14:16, color:"#5b7365", maxWidth:560, margin:"0 auto" }}>See how 6zero.ai stacks up against the systems holding your equity hostage.</p>
        </div>
        {isMobile ? (
          <div style={{ display:"grid", gap:14 }}>
            {features.map(([feat, sz, agent, portal]) => (
              <div key={feat} style={{ background:"#fff", borderRadius:18, padding:18, boxShadow:"0 6px 20px rgba(21,45,33,0.06)" }}>
                <div style={{ fontFamily:"'Playfair Display',serif", fontSize:17, fontWeight:700, color:"#152d21", marginBottom:12 }}>{feat}</div>
                <div style={{ display:"grid", gridTemplateColumns:"96px 1fr", rowGap:8, columnGap:10, fontSize:13 }}>
                  <div style={{ color:"#2b8259", fontWeight:700 }}>6zero.ai</div>
                  <div style={{ color:"#152d21", fontWeight:700 }}>{sz}</div>
                  <div style={{ color:"#9aa9a0" }}>Agents</div>
                  <div style={{ color:"#5b7365" }}>{agent}</div>
                  <div style={{ color:"#9aa9a0" }}>Portals</div>
                  <div style={{ color:"#5b7365" }}>{portal}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ background:"#fff", borderRadius:24, overflow:"hidden", boxShadow:"0 10px 40px rgba(21,45,33,0.06)" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:14 }}>
              <thead>
                <tr style={{ borderBottom:"1px solid #f0f4f2" }}>
                  {["Feature","6zero.ai","Traditional Agents","Legacy Portals"].map((h,i) => (
                    <th key={h} style={{ padding:"24px 28px", textAlign:"left", fontFamily:"'Playfair Display',serif", fontSize:i===1?20:16, color:i===1?"#2b8259":"#5b7365", background:i===1?"#f7faf8":"transparent", borderLeft:i===1?"1px solid #f0f4f2":undefined, borderRight:i===1?"1px solid #f0f4f2":undefined }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {features.map(([feat,...vals]) => (
                  <tr key={feat} style={{ borderBottom:"1px solid #f7f9f8" }}>
                    <td style={{ padding:"18px 28px", fontWeight:700, color:"#152d21" }}>{feat}</td>
                    {vals.map((v,i) => (
                      <td key={i} style={{ padding:"18px 28px", fontWeight:i===0?700:400, color:i===0?"#2b8259":"#5b7365", background:i===0?"#f7faf8":"transparent", borderLeft:i===0?"1px solid #f0f4f2":undefined, borderRight:i===0?"1px solid #f0f4f2":undefined }}>{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* CTA */}
      <section style={{ padding:isMobile?"50px 18px":"80px 40px", textAlign:"center" }}>
        <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(26px,5vw,44px)", fontWeight:700, color:"#152d21", margin:"0 0 14px" }}>
          Join the waitlist.<br/><span style={{ color:"#2b8259", fontStyle:"italic" }}>Keep your equity.</span>
        </h2>
        <p style={{ fontSize:15, color:"#5b7365", marginBottom:28 }}>We're onboarding buyers and sellers in the Bay Area first.</p>
        <button onClick={onJoinBeta} style={{ background:"#2b8259", color:"#fff", border:"none", borderRadius:12, padding:isMobile?"15px 32px":"18px 48px", fontWeight:700, fontSize:16, cursor:"pointer", boxShadow:"0 8px 24px rgba(43,130,89,0.3)" }}>
          Get Early Access →
        </button>
        <p style={{ marginTop:16, fontSize:13, color:"#5b7365" }}>
          Already have a code?{" "}
          <button onClick={onJoinBeta} style={{ background:"none", border:"none", color:"#2b8259", fontWeight:700, cursor:"pointer", fontSize:13 }}>Enter it here →</button>
        </p>
      </section>

      {/* Footer */}
      <footer style={{ textAlign:"center", paddingBottom:36, fontSize:13, color:"#5b7365" }}>
        <div style={{ fontFamily:"'DM Mono',monospace", fontSize:22, fontWeight:700, color:"#2b8259", marginBottom:8 }}>
          6zero.ai
        </div>
        © 2026 6zero.ai
      </footer>
    </div>
  );
}

// ─── BETA GATE ────────────────────────────────────────────────────────────────
function BetaGate({ onAccess, onBack }) {
  const [view, setView]       = useState("landing");
  const [email, setEmail]     = useState("");
  const [name, setName]       = useState("");
  const [code, setCode]       = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [done, setDone]       = useState(false);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) { setError("Enter a valid email."); return; }
    setLoading(true); setError("");
    try {
      await fetch("/api/signup", {
        method:"POST",
        headers:{ "Content-Type":"application/json" },
        body:JSON.stringify({ email, name }),
      });
      track("waitlist_signup_submitted", { email });
      setDone(true);
    } catch { setError("Something went wrong. Try again."); }
    setLoading(false);
  };

  const handleCode = (e) => {
    e.preventDefault();
    if (code.trim() === BETA_SECRET) {
      localStorage.setItem(BETA_KEY, BETA_SECRET);
      track("beta_gate_passed");
      captureLoginForensics("beta_gate", email || null);
      onAccess();
    } else {
      track("beta_gate_failed");
      setError("Invalid access code.");
    }
  };

  const inputStyle = {
    background:"#fff", border:"1.5px solid #d8ede6", borderRadius:10,
    padding:"12px 14px", fontSize:14, color:"#1c2b25", width:"100%",
    fontFamily:"'Outfit',sans-serif", transition:"border-color 0.18s",
  };

  return (
    <div style={{ minHeight:"100vh", background:"#f2f8f5", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"40px 24px", fontFamily:"'Outfit',sans-serif", position:"relative", overflow:"hidden" }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{ position:"absolute", top:"-80px", right:"-80px", width:360, height:360, background:"radial-gradient(circle, rgba(126,207,176,0.2) 0%, transparent 70%)", borderRadius:"50%", animation:"blob 8s ease-in-out infinite", pointerEvents:"none" }}/>
      <div style={{ position:"absolute", bottom:"-60px", left:"-60px", width:280, height:280, background:"radial-gradient(circle, rgba(92,77,177,0.08) 0%, transparent 70%)", borderRadius:"50%", animation:"blob 10s ease-in-out 2s infinite", pointerEvents:"none" }}/>

      <div style={{ position:"relative", zIndex:1, maxWidth:400, width:"100%", animation:"fadein 0.5s ease" }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:"#7aad98", cursor:"pointer", fontFamily:"'DM Mono',monospace", fontSize:11, letterSpacing:"0.1em", marginBottom:24, padding:0 }}>← back</button>

        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:36, justifyContent:"center" }}>
          <Logo size={40}/>
          <div>
            <BrandLabel/>
            <div style={{ fontWeight:300, fontSize:12, color:"#6a8a7e" }}>Real estate, without the middleman</div>
          </div>
        </div>

        {view === "landing" && (
          <div style={{ textAlign:"center" }}>
            <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:"#e4f7ef", border:"1px solid #b8e8d4", borderRadius:20, padding:"5px 14px", marginBottom:20 }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:"#2d7a62" }}/>
              <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:"0.14em", color:"#2d7a62", textTransform:"uppercase" }}>Private Beta</span>
            </div>
            <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:34, fontWeight:700, color:"#1c2b25", margin:"0 0 14px", lineHeight:1.1 }}>
              Skip the agent.<br/><em style={{ color:"#2d7a62", fontStyle:"italic", fontWeight:500 }}>Keep the edge.</em>
            </h1>
            <p style={{ fontWeight:300, color:"#5a7a6e", fontSize:14, lineHeight:1.75, margin:"0 auto 32px", maxWidth:300 }}>
              6zero.ai is currently invite-only. Request early access or enter your beta code.
            </p>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <button className="btn-p" onClick={() => setView("signup")} style={{ background:"#2d7a62", color:"#fff", border:"none", borderRadius:10, padding:"13px 0", fontSize:14, fontWeight:500, cursor:"pointer", width:"100%" }}>
                Request early access
              </button>
              <button className="btn-g" onClick={() => setView("verify")} style={{ background:"transparent", color:"#7aad98", border:"1.5px solid #c0ddd4", borderRadius:10, padding:"12px 0", fontSize:12, cursor:"pointer", width:"100%", fontFamily:"'DM Mono',monospace", letterSpacing:"0.06em" }}>
                I have an access code →
              </button>
              <div style={{ borderTop:"1px solid #e0ede8", paddingTop:10, marginTop:2 }}>
                <button onClick={() => window.location.href="/join"} style={{ background:"transparent", color:"#2d7a62", border:"1.5px solid #2d7a62", borderRadius:10, padding:"12px 0", fontSize:13, fontWeight:600, cursor:"pointer", width:"100%", fontFamily:"'Outfit',sans-serif" }}>
                  I'm a vendor — list my services →
                </button>
              </div>
            </div>
          </div>
        )}

        {view === "signup" && !done && (
          <div>
            <button onClick={() => { setView("landing"); setError(""); }} style={{ background:"none", border:"none", color:"#7aad98", cursor:"pointer", fontFamily:"'DM Mono',monospace", fontSize:11, letterSpacing:"0.1em", marginBottom:24, padding:0 }}>← back</button>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:700, color:"#1c2b25", margin:"0 0 6px" }}>Request access</h2>
            <p style={{ fontWeight:300, color:"#7a9a90", fontSize:13, marginBottom:28, lineHeight:1.65 }}>We'll send your access code when a spot opens up.</p>
            <form onSubmit={handleSignup} style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name (optional)" style={inputStyle} onFocus={e=>e.target.style.borderColor="#2d7a62"} onBlur={e=>e.target.style.borderColor="#d8ede6"}/>
              <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email address" type="email" required style={inputStyle} onFocus={e=>e.target.style.borderColor="#2d7a62"} onBlur={e=>e.target.style.borderColor="#d8ede6"}/>
              {error && <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:"#c0354a" }}>{error}</div>}
              <button type="submit" disabled={loading} className="btn-p" style={{ background:"#2d7a62", color:"#fff", border:"none", borderRadius:10, padding:"13px 0", fontSize:14, fontWeight:500, cursor:"pointer", width:"100%", opacity:loading?0.6:1, marginTop:4 }}>
                {loading ? "Submitting…" : "Join the waitlist"}
              </button>
            </form>
          </div>
        )}

        {view === "signup" && done && (
          <div style={{ textAlign:"center" }}>
            <div style={{ width:52, height:52, borderRadius:"50%", background:"#e4f7ef", border:"2px solid #b8e8d4", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, margin:"0 auto 18px" }}>✓</div>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:24, fontWeight:700, color:"#1c2b25", margin:"0 0 10px" }}>You're on the list</h2>
            <p style={{ fontWeight:300, color:"#7a9a90", fontSize:14, lineHeight:1.7, marginBottom:28 }}>
              We'll email <strong style={{ color:"#1c2b25", fontWeight:500 }}>{email}</strong> with your code when a spot opens.
            </p>
            <button onClick={() => { setView("verify"); setError(""); }} className="btn-g" style={{ background:"transparent", color:"#7aad98", border:"1.5px solid #c0ddd4", borderRadius:10, padding:"11px 24px", fontSize:12, cursor:"pointer", fontFamily:"'DM Mono',monospace", letterSpacing:"0.06em" }}>
              Already have a code? →
            </button>
          </div>
        )}

        {view === "verify" && (
          <div>
            <button onClick={() => { setView("landing"); setError(""); }} style={{ background:"none", border:"none", color:"#7aad98", cursor:"pointer", fontFamily:"'DM Mono',monospace", fontSize:11, letterSpacing:"0.1em", marginBottom:24, padding:0 }}>← back</button>
            <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:26, fontWeight:700, color:"#1c2b25", margin:"0 0 6px" }}>Enter access code</h2>
            <p style={{ fontWeight:300, color:"#7a9a90", fontSize:13, marginBottom:28 }}>Paste the code from your invite.</p>
            <form onSubmit={handleCode} style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <input value={code} onChange={e=>setCode(e.target.value)} placeholder="Enter your code" style={{ ...inputStyle, fontFamily:"'DM Mono',monospace", letterSpacing:"0.06em" }} onFocus={e=>e.target.style.borderColor="#2d7a62"} onBlur={e=>e.target.style.borderColor="#d8ede6"}/>
              {error && <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:"#c0354a" }}>{error}</div>}
              <button type="submit" className="btn-p" style={{ background:"#2d7a62", color:"#fff", border:"none", borderRadius:10, padding:"13px 0", fontSize:14, fontWeight:500, cursor:"pointer", width:"100%" }}>
                Enter →
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

const TypingDots = () => (
  <div style={{ display:"flex", gap:5, padding:"4px 2px", alignItems:"center" }}>
    {[0,1,2].map(i => <div key={i} style={{ width:7, height:7, borderRadius:"50%", background:"#2d7a62", animation:`tdot 1.1s ease-in-out ${i*0.18}s infinite` }}/>)}
  </div>
);

const Bubble = ({ msg }) => {
  const isUser = msg.role === "user";
  const formatted = msg.content
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/^- /gm, '• ')
    .replace(/\n/g, '<br/>');

  return (
    <div style={{ display:"flex", justifyContent:isUser?"flex-end":"flex-start", marginBottom:16, animation:"rise 0.25s ease" }}>
      {!isUser && <div style={{ marginRight:8, marginTop:2, flexShrink:0 }}><Logo size={26}/></div>}
      <div style={{
        maxWidth:"min(86%, 640px)",
        background:isUser?"linear-gradient(135deg,#1c2b25,#2d4a3a)":"#fff",
        color:isUser?"#d4f5e8":"#1c2b25",
        borderRadius:isUser?"20px 20px 5px 20px":"20px 20px 20px 5px",
        padding:"13px 17px", fontSize:14, lineHeight:1.7,
        fontFamily:"'Outfit',sans-serif", fontWeight:400,
        boxShadow:isUser?"0 4px 18px rgba(28,43,37,0.2)":"0 2px 12px rgba(0,0,0,0.07)",
        border:isUser?"none":"1px solid #e0ede8",
      }}
        dangerouslySetInnerHTML={{ __html: isUser ? msg.content.replace(/\n/g,'<br/>') : formatted }}
      />
    </div>
  );
};

const PropertyCard = ({ data }) => {
  if (!data) return null;
  const p = data?.property?.[0];
  if (!p) return null;
  const b = p.building;
  const t = p.assessment?.assessed;
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ background:"#fff", border:"1.5px solid #b8e8d4", borderRadius:14, padding:"14px 16px", boxShadow:"0 2px 10px rgba(45,122,98,0.08)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
          <div style={{ width:8, height:8, borderRadius:"50%", background:"#2d7a62" }}/>
          <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:"0.12em", color:"#2d7a62", textTransform:"uppercase" }}>Property Data — Attom</span>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          {[
            ["Beds", b?.rooms?.beds || "—"],
            ["Baths", b?.rooms?.bathstotal || "—"],
            ["Sqft", b?.size?.bldgsize ? b.size.bldgsize.toLocaleString() : "—"],
            ["Year Built", b?.yearbuilt || "—"],
            ["Lot Sqft", p.lot?.lotsize2 ? p.lot.lotsize2.toLocaleString() : "—"],
            ["Assessed", t?.assdttlvalue ? "$"+Number(t.assdttlvalue).toLocaleString() : "—"],
          ].map(([label, val]) => (
            <div key={label} style={{ textAlign:"center" }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:16, fontWeight:700, color:"#1c2b25" }}>{val}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:9, color:"#9ab0a8", letterSpacing:"0.08em", textTransform:"uppercase", marginTop:2 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const isAddress = (text) =>
  /\d+\s+\w+.*(st|ave|blvd|dr|ct|rd|ln|way|pl|circle|court|drive|street|avenue|road|lane)\b/i.test(text) ||
  /\d+\s+\w+.*,\s*\w+.*\d{5}/i.test(text);

export default function Sixzero() {
  const [screen, setScreen] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get('token');
    if (tokenFromUrl === BETA_SECRET) {
      localStorage.setItem(BETA_KEY, BETA_SECRET);
      captureLoginForensics("beta_gate_url_token");
      return "app";
    }
    if (localStorage.getItem(BETA_KEY) === BETA_SECRET) {
      // Existing session — fire forensics once per browser session, not per reload.
      if (!sessionStorage.getItem("od_session_logged")) {
        sessionStorage.setItem("od_session_logged", "1");
        captureLoginForensics("beta_gate");
      }
      return "app";
    }
    return "landing";
  });

  const [mode, setMode]                 = useState(null);
  const [messages, setMessages]         = useState([]);
  const [input, setInput]               = useState(() => {
    const a = new URLSearchParams(window.location.search).get("address");
    return a ? `${a}\n\nWhat's a fair price?` : "";
  });
  const [loading, setLoading]           = useState(false);
  const [stage, setStage]               = useState("valuation");
  const [propertyData, setPropertyData] = useState(null);
  const [lookingUp, setLookingUp]       = useState(false);
  const [sessionId, setSessionId]       = useState(null);
  const [menuOpen, setMenuOpen]         = useState(false);
  const endRef = useRef(null);
  const taRef  = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages, loading]);

  // Hard sandbox: a signed-in broker must never render the consumer
  // landing/chat site — not just as a UX nicety, but because AuthGate's
  // syncProfile overwrites profiles.role on every auth-gated route a user
  // visits, so a broker who so much as flashes through /buy or /sell would
  // silently get demoted from broker to buyer/seller. This is the actual
  // fix for magic links landing here instead of /broker (Supabase's
  // redirect-URL allow-list can misconfigure again in the future — this
  // check doesn't depend on that being right).
  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    supabase.auth.getSession().then(async ({ data }) => {
      const uid = data?.session?.user?.id;
      if (!uid || cancelled) return;
      const { data: profile } = await supabase.from("profiles").select("role").eq("id", uid).maybeSingle();
      if (!cancelled && profile?.role === "broker") {
        window.location.replace("/broker");
      }
    });
    return () => { cancelled = true; };
  }, []);

  if (screen === "landing") return <LandingPage onJoinBeta={() => setScreen("beta")} />;
  if (screen === "beta") return <BetaGate onAccess={() => setScreen("app")} onBack={() => setScreen("landing")} />;

  const lookupProperty = async (address) => {
    setLookingUp(true);
    track("property_lookup_requested", { address });
    try {
      const res = await fetch("/api/property", {
        method:"POST",
        headers:{ "Content-Type":"application/json", "x-beta-token": BETA_SECRET },
        body:JSON.stringify({ address }),
      });
      const data = await res.json();
      if (data?.property?.[0]) {
        setPropertyData(data);
        track("property_card_viewed", { address });
        return data;
      }
    } catch {}
    setLookingUp(false);
    return null;
  };

  const formatPropertyForAI = (data) => {
    const p = data?.property?.[0];
    if (!p) return "";
    const b = p.building;
    const t = p.assessment?.assessed;
    return `\n\n[PROPERTY DATA FROM ATTOM]\nBeds: ${b?.rooms?.beds||"?"} | Baths: ${b?.rooms?.bathstotal||"?"} | Sqft: ${b?.size?.bldgsize||"?"} | Year Built: ${b?.yearbuilt||"?"} | Lot: ${p.lot?.lotsize2||"?"} sqft | Assessed Value: $${t?.assdttlvalue ? Number(t.assdttlvalue).toLocaleString() : "?"}`;
  };

  const send = async (text, opts = {}) => {
    let enrichedText = text;
    if (isAddress(text) && !propertyData) {
      const data = await lookupProperty(text);
      if (data) enrichedText = text + formatPropertyForAI(data);
    }
    const next = [...messages, { role:"user", content:text }];
    const apiMessages = [...messages, { role:"user", content:enrichedText }];
    setMessages(next);
    setInput("");
    if (taRef.current) taRef.current.style.height = "24px";
    setLoading(true);
    setLookingUp(false);
    track("message_sent", { stage: opts.stage ?? stage, mode: opts.mode ?? mode?.id });
    try {
      const res = await fetch("/api/chat", {
        method:"POST",
        headers:{ "Content-Type":"application/json", "x-beta-token": BETA_SECRET },
        body:JSON.stringify({
          model:"claude-haiku-4-5-20251001",
          max_tokens:1000,
          system:SYSTEM_PROMPT,
          messages:apiMessages.filter((m,i,arr) => i===0 || m.role !== arr[i-1].role),
          sessionId,
          mode: opts.mode ?? mode?.id ?? null,
          stage: opts.stage ?? stage,
        }),
      });
      const data = await res.json();
      if (data.sessionId && data.sessionId !== sessionId) setSessionId(data.sessionId);
      const reply = data.content?.map(b => b.text||"").join("") || "Something went wrong.";
      setMessages(prev => [...prev, { role:"assistant", content:reply }]);
    } catch {
      setMessages(prev => [...prev, { role:"assistant", content:"Connection error. Please try again." }]);
    }
    setLoading(false);
  };

  const startMode = (s) => {
    track("mode_selected", { mode: s.id });
    if (s.id === "preferences" || s.id === "offer") {
      // Buyer flow lives at /buy — search + map + cards experience, with a
      // real "Make an offer" on a real listing. This used to fall through
      // to a generic canned chat with no property attached — not connected
      // to the real offer system built into the buyer flow.
      window.location.href = "/buy";
      return;
    }
    if (s.id === "seller") {
      // Seller flow lives at /sell/v2. Force the dashboard (my listings +
      // start new) instead of resuming whatever journey was last touched —
      // the landing page is an entry point, not a "continue where I left
      // off" shortcut.
      window.location.href = "/sell/v2?view=dashboard";
      return;
    }
    setMode(s);
    setMessages([]);
    setPropertyData(null);
    setSessionId(null);
    send(s.prompt, { mode: s.id });
  };
  const currentMode = STARTER_MODES.find(s => s.id === mode);

  if (!mode) return (
    <div style={{ minHeight:"100vh", background:"#f2f8f5", fontFamily:"'Outfit',sans-serif", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:isMobile?"32px 18px":"48px 24px", position:"relative", overflow:"hidden" }}>
      <style>{GLOBAL_CSS}</style>
      <div style={{ position:"absolute", top:"-80px", right:"-80px", width:380, height:380, background:"radial-gradient(circle, rgba(126,207,176,0.18) 0%, transparent 70%)", borderRadius:"50%", animation:"blob 8s ease-in-out infinite", pointerEvents:"none" }}/>
      <div style={{ position:"absolute", bottom:"-60px", left:"-60px", width:300, height:300, background:"radial-gradient(circle, rgba(92,77,177,0.08) 0%, transparent 70%)", borderRadius:"50%", animation:"blob 10s ease-in-out 2s infinite", pointerEvents:"none" }}/>

      <div style={{ position:"relative", zIndex:1, maxWidth:620, width:"100%", animation:"fadein 0.55s ease" }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:50 }}>
          <Logo size={38}/>
          <div>
            <BrandLabel/>
            <div style={{ fontWeight:300, fontSize:12, color:"#6a8a7e" }}>Real estate, without the middleman</div>
          </div>
        </div>

        <div style={{ marginBottom:isMobile?32:44 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:"#e4f7ef", border:"1px solid #b8e8d4", borderRadius:20, padding:"5px 14px", marginBottom:18 }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:"#2d7a62" }}/>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:"0.14em", color:"#2d7a62", textTransform:"uppercase" }}>Private Beta</span>
          </div>
          <h1 style={{ fontFamily:"'Playfair Display',serif", fontSize:"clamp(30px,7vw,54px)", fontWeight:700, color:"#1c2b25", margin:"0 0 14px", lineHeight:1.1, letterSpacing:"-0.02em" }}>
            Buy. Sell. Negotiate.<br/>
            <em style={{ color:"#2d7a62", fontStyle:"italic", fontWeight:500 }}>No agent required.</em>
          </h1>
          <p style={{ fontWeight:300, color:"#5a7a6e", fontSize:isMobile?14:15, maxWidth:420, lineHeight:1.75, margin:0 }}>
            AI-powered valuation, deal analysis, and offer writing. Everything your agent did — now in your hands.
          </p>
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:isMobile?10:12, marginBottom:isMobile?28:40 }}>
          {STARTER_MODES.map(s => (
            <button key={s.id} className="mcard" onClick={() => startMode(s)} style={{ background:"#fff", border:"1.5px solid rgba(0,0,0,0.07)", borderRadius:16, padding:isMobile?"16px 14px 14px":"22px 20px 18px", textAlign:"left", boxShadow:"0 2px 10px rgba(0,0,0,0.06)" }}>
              <div style={{ width:isMobile?32:38, height:isMobile?32:38, borderRadius:10, background:s.color, display:"flex", alignItems:"center", justifyContent:"center", fontSize:isMobile?15:17, color:s.accent, marginBottom:isMobile?10:13, fontFamily:"'DM Mono',monospace" }}>{s.icon}</div>
              <div style={{ fontSize:isMobile?14:15, fontWeight:600, color:"#1c2b25", marginBottom:4 }}>{s.label}</div>
              <div style={{ fontWeight:300, fontSize:isMobile?11:12, color:"#9ab0a8", lineHeight:1.4 }}>{s.description}</div>
            </button>
          ))}
        </div>

        <div style={{ display:"flex", gap:10, marginBottom:isMobile?16:20, flexWrap:"wrap" }}>
          <button onClick={() => window.location.href="/marketplace"} style={{ flex:1, background:"#fff", border:"1.5px solid rgba(45,122,98,0.2)", borderRadius:12, padding:"12px 16px", fontSize:13, fontWeight:600, color:"#2d7a62", cursor:"pointer", fontFamily:"'Outfit',sans-serif", textAlign:"center" }}>
            Browse Marketplace
          </button>
          <button onClick={() => window.location.href="/join"} style={{ flex:1, background:"#2d7a62", border:"none", borderRadius:12, padding:"12px 16px", fontSize:13, fontWeight:600, color:"#fff", cursor:"pointer", fontFamily:"'Outfit',sans-serif", textAlign:"center" }}>
            Join as a Vendor →
          </button>
        </div>

        <div style={{ display:"flex", background:"#fff", borderRadius:14, border:"1.5px solid rgba(0,0,0,0.06)", overflow:"hidden", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
          {[["6%","avg agent commission"],["$18k","savings on $300k home"],["0","middlemen involved"]].map(([v,l],i) => (
            <div key={v} style={{ flex:1, padding:isMobile?"14px 4px":"18px 0", textAlign:"center", borderRight:i<2?"1px solid #edf2ef":"none" }}>
              <div style={{ fontFamily:"'Playfair Display',serif", fontSize:isMobile?20:26, fontWeight:700, color:"#2d7a62", fontStyle:"italic" }}>{v}</div>
              <div style={{ fontFamily:"'DM Mono',monospace", fontSize:isMobile?9:10, color:"#b0c8be", letterSpacing:"0.06em", marginTop:4, textTransform:"uppercase", lineHeight:1.5, padding:"0 6px" }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ height:"100vh", background:"#f2f8f5", display:"flex", flexDirection:"column", fontFamily:"'Outfit',sans-serif" }}>
      <style>{GLOBAL_CSS}</style>

      <div style={{ padding:isMobile?"10px 12px":"12px 20px", borderBottom:"1px solid rgba(45,122,98,0.12)", display:"flex", alignItems:"center", justifyContent:"space-between", background:"rgba(242,248,245,0.97)", backdropFilter:"blur(10px)", flexShrink:0, gap:8 }}>
        <div style={{ display:"flex", alignItems:"center", gap:isMobile?6:10, cursor:"pointer", flexShrink:0 }} onClick={() => { setMode(null); setMessages([]); setPropertyData(null); }}>
          <Logo size={isMobile?24:28}/>
          {!isMobile && <BrandLabel/>}
        </div>
        <div style={{ display:"flex", gap:2, background:"rgba(45,122,98,0.08)", borderRadius:22, padding:3 }}>
          {[["valuation","Value"],["deal","Deal"],["offer","Offer"]].map(([s,l]) => (
            <button key={s} className="stab" onClick={() => setStage(s)} style={{ padding:isMobile?"4px 10px":"5px 14px", borderRadius:18, fontSize:isMobile?10:11, fontFamily:"'DM Mono',monospace", letterSpacing:"0.06em", border:"none", cursor:"pointer", background:stage===s?"#1c2b25":"transparent", color:stage===s?"#d4f5e8":"#7aad98" }}>{l}</button>
          ))}
        </div>
        <div style={{ display:"flex", gap:6, flexShrink:0, position:"relative" }}>
          <button onClick={() => setMenuOpen(o => !o)} style={{ background:"transparent", border:"1px solid rgba(45,122,98,0.25)", color:"#5a8a78", borderRadius:8, padding:"5px 12px", fontSize:18, cursor:"pointer", lineHeight:1 }}>≡</button>
          {menuOpen && (
            <>
              <div onClick={() => setMenuOpen(false)} style={{ position:"fixed", inset:0, zIndex:99 }} />
              <div style={{ position:"absolute", top:"calc(100% + 8px)", right:0, background:"#fff", border:"1px solid #d8ede6", borderRadius:12, padding:8, zIndex:100, minWidth:180, boxShadow:"0 8px 24px rgba(0,0,0,0.12)", display:"flex", flexDirection:"column", gap:2 }}>
                {[
                  ["Buy", "/buy"],
                  ["Sell", "/sell/v2?view=dashboard"],
                  ["Browse Marketplace", "/marketplace"],
                  ["List your services", "/join"],
                ].map(([label, href]) => (
                  <button key={href} onClick={() => window.location.href = href} style={{ background:"none", border:"none", textAlign:"left", padding:"10px 14px", fontSize:14, color:"#1c2b25", cursor:"pointer", borderRadius:8, fontFamily:"'Outfit',sans-serif", fontWeight:500 }}
                    onMouseEnter={e => e.target.style.background="#f2f8f5"}
                    onMouseLeave={e => e.target.style.background="none"}
                  >{label}</button>
                ))}
                <div style={{ height:1, background:"#eef3ef", margin:"4px 0" }} />
                <button onClick={() => { setMenuOpen(false); setMode(null); setMessages([]); setPropertyData(null); }} style={{ background:"none", border:"none", textAlign:"left", padding:"10px 14px", fontSize:14, color:"#5b7365", cursor:"pointer", borderRadius:8, fontFamily:"'Outfit',sans-serif" }}
                  onMouseEnter={e => e.target.style.background="#f2f8f5"}
                  onMouseLeave={e => e.target.style.background="none"}
                >← Home</button>
              </div>
            </>
          )}
        </div>
      </div>

      {currentMode && (
        <div style={{ padding:isMobile?"8px 14px 0":"10px 20px 0", maxWidth:700, width:"100%", margin:"0 auto", alignSelf:"stretch", display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:7, background:currentMode.color, border:`1px solid ${currentMode.accent}33`, borderRadius:20, padding:"4px 12px" }}>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:13, color:currentMode.accent }}>{currentMode.icon}</span>
            <span style={{ fontFamily:"'DM Mono',monospace", fontSize:10, letterSpacing:"0.1em", color:currentMode.accent, textTransform:"uppercase" }}>{currentMode.label}</span>
          </div>
          {mode === "listing" && (
            <button
              onClick={() => (window.location.href = "/sell/v2?view=dashboard")}
              style={{ background:"none", border:"none", cursor:"pointer", fontFamily:"'DM Mono',monospace", fontSize:11, color:"#2b8259", textDecoration:"underline", letterSpacing:"0.02em" }}
            >Selling instead? Go to the seller experience →</button>
          )}
        </div>
      )}

      <div style={{ flex:1, overflowY:"auto", padding:isMobile?"14px 14px 8px":"20px 20px 8px", maxWidth:700, width:"100%", margin:"0 auto", alignSelf:"stretch" }}>
        {propertyData && <PropertyCard data={propertyData}/>}
        {messages.map((m,i) => <Bubble key={i} msg={m}/>)}
        {(loading || lookingUp) && (
          <div style={{ display:"flex", alignItems:"center", marginBottom:14 }}>
            <div style={{ marginRight:10, flexShrink:0 }}><Logo size={28}/></div>
            <div style={{ background:"#fff", border:"1px solid #e0ede8", borderRadius:"20px 20px 20px 5px", padding:"12px 16px", boxShadow:"0 2px 10px rgba(0,0,0,0.07)" }}>
              {lookingUp
                ? <span style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:"#7aad98" }}>Looking up property…</span>
                : <TypingDots/>}
            </div>
          </div>
        )}
        <div ref={endRef}/>
      </div>

      <div style={{ padding:isMobile?"6px 14px 2px":"6px 20px 2px", display:"flex", gap:7, overflowX:"auto", scrollbarWidth:"none", maxWidth:700, width:"100%", margin:"0 auto", alignSelf:"stretch" }}>
        {QUICK[stage].map(q => (
          <button key={q} className="chip" onClick={() => send(q)} style={{ background:"#fff", border:"1.5px solid rgba(45,122,98,0.18)", color:"#5a8a78", borderRadius:20, padding:"6px 14px", fontSize:11, cursor:"pointer", fontFamily:"'DM Mono',monospace", letterSpacing:"0.04em", whiteSpace:"nowrap", flexShrink:0, boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>{q}</button>
        ))}
      </div>

      <div style={{ padding:isMobile?"8px 12px 12px":"8px 16px 18px", maxWidth:700, width:"100%", margin:"0 auto", alignSelf:"stretch" }}>
        <div style={{ background:"#fff", border:"1.5px solid rgba(45,122,98,0.2)", borderRadius:18, padding:isMobile?"10px 12px":"14px 16px", display:"flex", alignItems:"flex-end", gap:isMobile?8:12, boxShadow:"0 3px 14px rgba(45,122,98,0.08)", transition:"border-color 0.2s, box-shadow 0.2s" }}
          onFocusCapture={e=>{ e.currentTarget.style.borderColor="#2d7a62"; e.currentTarget.style.boxShadow="0 3px 20px rgba(45,122,98,0.15)"; }}
          onBlurCapture={e=>{ e.currentTarget.style.borderColor="rgba(45,122,98,0.2)"; e.currentTarget.style.boxShadow="0 3px 14px rgba(45,122,98,0.08)"; }}
        >
          <textarea ref={taRef} rows={1} placeholder="Type an address or ask anything…" value={input}
            onChange={e=>{ setInput(e.target.value); e.target.style.height="auto"; e.target.style.height=Math.min(e.target.scrollHeight,120)+"px"; }}
            onKeyDown={e=>{ if(e.key==="Enter"&&!e.shiftKey){ e.preventDefault(); if(input.trim()&&!loading) send(input.trim()); }}}
            style={{ minHeight:24, maxHeight:120 }}
          />
          <button onClick={()=>{ if(input.trim()&&!loading) send(input.trim()); }} disabled={loading||!input.trim()} style={{ width:36, height:36, borderRadius:11, flexShrink:0, background:input.trim()&&!loading?"linear-gradient(135deg,#2d7a62,#1c5448)":"rgba(45,122,98,0.08)", border:"none", cursor:input.trim()&&!loading?"pointer":"default", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, color:input.trim()&&!loading?"#d4f5e8":"#9bbfb2", transition:"all 0.2s", boxShadow:input.trim()&&!loading?"0 3px 12px rgba(45,122,98,0.3)":"none" }}>↑</button>
        </div>
      </div>
    </div>
  );
}